import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { NotificationApi } from "@/api";

// Интервал polling'а счётчика непрочитанных (websocket в проекте нет)
export const NOTICES_POLL_INTERVAL = 60000;

export const useNotificationStore = defineStore("notification", () => {
  // State
  const notifications = ref([]);
  const noticesCount = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const pagination = ref({
    page: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });
  let pollTimer = null;

  // Getters
  const unreadNotifications = computed(() => notifications.value.filter((n) => !n.read_at));
  const hasUnread = computed(() => (noticesCount.value || 0) > 0);

  // Actions
  /**
   * Получение количества непрочитанных уведомлений
   */
  async function fetchNoticesCount() {
    try {
      const data = await NotificationApi.getNoticesCount();
      // Бэк отдаёт { count } либо { data: { count } }
      noticesCount.value = data?.count ?? data?.data?.count ?? 0;
      return noticesCount.value;
    } catch (err) {
      console.error("Ошибка при получении количества уведомлений:", err);
      error.value = err;
      return noticesCount.value;
    }
  }

  /**
   * Получение уведомлений
   * @param {Object} params - { page, per_page, unread }
   */
  async function fetchNotifications(params = {}) {
    loading.value = true;
    error.value = null;

    try {
      const response = await NotificationApi.getNotifications({
        per_page: pagination.value.perPage,
        ...params,
      });

      // Пагинированный ответ { data, meta } либо голый массив
      const list = Array.isArray(response) ? response : response?.data || [];
      notifications.value = list;

      const meta = response?.meta;
      if (meta) {
        pagination.value = {
          page: meta.current_page || 1,
          lastPage: meta.last_page || 1,
          total: meta.total || list.length,
          perPage: meta.per_page || pagination.value.perPage,
        };
      }

      return notifications.value;
    } catch (err) {
      console.error("Ошибка при получении уведомлений:", err);
      error.value = err;
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Отметить уведомление как прочитанное
   * @param {Number|String} id - ID уведомления
   */
  async function markAsRead(id) {
    const notification = notifications.value.find((notice) => notice.id === id);

    // Уже прочитано — на сервер не ходим
    if (notification && notification.read_at) return notification;

    try {
      await NotificationApi.markAsRead(id);

      if (notification) {
        notification.read_at = new Date().toISOString();
      }
      if (noticesCount.value) {
        noticesCount.value = Math.max(0, noticesCount.value - 1);
      }

      return notification;
    } catch (err) {
      console.error(`Ошибка при отметке уведомления ${id} как прочитанного:`, err);
      error.value = err;
      throw err;
    }
  }

  /**
   * Отметить все уведомления как прочитанные
   */
  async function markAllAsRead() {
    try {
      await NotificationApi.markAllAsRead();

      const now = new Date().toISOString();
      notifications.value.forEach((notification) => {
        if (!notification.read_at) {
          notification.read_at = now;
        }
      });
      noticesCount.value = 0;
    } catch (err) {
      console.error("Ошибка при отметке всех уведомлений как прочитанных:", err);
      error.value = err;
      throw err;
    }
  }

  /**
   * Запуск periodic polling'а счётчика непрочитанных
   * @param {Number} interval - мс
   */
  function startPolling(interval = NOTICES_POLL_INTERVAL) {
    stopPolling();
    fetchNoticesCount();
    pollTimer = setInterval(fetchNoticesCount, interval);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function resetStore() {
    stopPolling();
    notifications.value = [];
    noticesCount.value = null;
    loading.value = false;
    error.value = null;
    pagination.value = { page: 1, lastPage: 1, total: 0, perPage: 20 };
  }

  return {
    // State
    notifications,
    noticesCount,
    loading,
    error,
    pagination,
    // Getters
    unreadNotifications,
    hasUnread,
    // Actions
    fetchNoticesCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    startPolling,
    stopPolling,
    resetStore,
  };
});
